import { useState, useCallback } from 'react';
import {
  Page,
  Card,
  DataTable,
  Button,
  Modal,
  FormLayout,
  TextField,
  Select,
  DropZone,
  Banner,
  Toast,
  Frame,
  BlockStack,
  InlineStack,
  Text,
  List,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';
import { useShop } from '../hooks/useShop';
import { useCurrency } from '../hooks/useCurrency';
import { client } from '../api/client';
import { Endpoints } from '../api/endpoints';

interface SpendRow {
  id: number;
  platform: string;
  campaign_name: string | null;
  date: string;
  amount: number | string;
  // impressions/clicks are only populated by the ad-platform API sync (disabled until
  // credentials exist); manual/CSV entry has no such fields, so those columns are hidden.
}

interface SpendResponse {
  data: SpendRow[];
}
interface SummaryResponse {
  total: number;
  by_platform: { platform: string; total: number }[];
}

const PLATFORMS = [
  { label: 'Facebook', value: 'facebook' },
  { label: 'Google', value: 'google' },
  { label: 'TikTok', value: 'tiktok' },
  { label: 'Other', value: 'other' },
];

const today = new Date().toISOString().slice(0, 10);

/** Parse a CSV string → rows for the batch endpoint. Columns (header optional): date, platform, amount, campaign. */
function parseCsv(text: string): { platform: string; date: string; amount: number; campaign_name?: string }[] {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const rows: { platform: string; date: string; amount: number; campaign_name?: string }[] = [];
  for (const line of lines) {
    const cols = line.split(',').map((c) => c.trim());
    // Skip a header row.
    if (/date/i.test(cols[0]) && /platform|amount/i.test(line)) continue;
    const [date, platform, amount, campaign] = cols;
    const amt = parseFloat(amount);
    if (!date || !platform || Number.isNaN(amt)) continue;
    rows.push({
      date,
      platform: platform.toLowerCase(),
      amount: amt,
      campaign_name: campaign || undefined,
    });
  }
  return rows;
}

export default function AdSpend() {
  const { shop } = useShop();
  const { formatMoney } = useCurrency(shop?.currency ?? 'USD');

  const list = useApi<SpendResponse>(Endpoints.adSpend.list);
  const summary = useApi<SummaryResponse>(Endpoints.adSpend.summary);

  const [manualOpen, setManualOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  // Manual form
  const [platform, setPlatform] = useState('facebook');
  const [date, setDate] = useState(today);
  const [amount, setAmount] = useState('');
  const [campaign, setCampaign] = useState('');

  // CSV
  const [csvRows, setCsvRows] = useState<ReturnType<typeof parseCsv>>([]);
  const [csvError, setCsvError] = useState<string | null>(null);

  const refresh = () => {
    list.refetch();
    summary.refetch();
  };

  const handleSaveManual = async () => {
    setSaving(true);
    try {
      await client.post(Endpoints.adSpend.create, {
        platform,
        date,
        amount: parseFloat(amount || '0'),
        campaign_name: campaign || null,
      });
      setToast('Ad spend added');
      setManualOpen(false);
      setAmount('');
      setCampaign('');
      refresh();
    } catch (e) {
      setToast((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleDropCsv = useCallback((_dropFiles: File[], acceptedFiles: File[]) => {
    setCsvError(null);
    const file = acceptedFiles[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const rows = parseCsv(String(reader.result ?? ''));
      if (rows.length === 0) {
        setCsvError('No valid rows found. Expected columns: date, platform, amount, campaign.');
      }
      setCsvRows(rows);
    };
    reader.readAsText(file);
  }, []);

  const handleImportCsv = async () => {
    if (csvRows.length === 0) return;
    setSaving(true);
    try {
      const res = await client.post<{ imported: number }>(Endpoints.adSpend.batch, { rows: csvRows });
      setToast(`Imported ${res.data.imported} rows`);
      setImportOpen(false);
      setCsvRows([]);
      refresh();
    } catch (e) {
      setToast((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    await client.delete(Endpoints.adSpend.remove(id));
    setToast('Deleted');
    refresh();
  };

  const rows =
    list.data?.data?.map((r) => [
      new Date(r.date).toLocaleDateString(),
      r.platform,
      r.campaign_name ?? '—',
      formatMoney(r.amount),
      <Button variant="plain" tone="critical" onClick={() => handleDelete(r.id)}>
        Delete
      </Button>,
    ]) ?? [];

  return (
    <Frame>
      <Page
        title="Ad Spend"
        subtitle="Log ad spend manually or via CSV — no ad platform API required"
        primaryAction={{ content: 'Add spend', onAction: () => setManualOpen(true) }}
        secondaryActions={[{ content: 'Import CSV', onAction: () => setImportOpen(true) }]}
      >
        <BlockStack gap="400">
          <Card>
            <InlineStack align="space-between" blockAlign="center">
              <Text as="h3" variant="headingMd">
                Total (last 30 days)
              </Text>
              <Text as="span" variant="headingLg">
                {formatMoney(summary.data?.total ?? 0)}
              </Text>
            </InlineStack>
          </Card>

          <Card>
            <DataTable
              columnContentTypes={['text', 'text', 'text', 'numeric', 'text']}
              headings={['Date', 'Platform', 'Campaign', 'Amount', '']}
              rows={rows}
            />
          </Card>
        </BlockStack>
      </Page>

      {/* Manual entry */}
      <Modal
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        title="Add ad spend"
        primaryAction={{ content: 'Save', onAction: handleSaveManual, loading: saving }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setManualOpen(false) }]}
      >
        <Modal.Section>
          <FormLayout>
            <FormLayout.Group>
              <Select label="Platform" options={PLATFORMS} value={platform} onChange={setPlatform} />
              <TextField
                label="Amount"
                type="number"
                prefix="$"
                value={amount}
                onChange={setAmount}
                autoComplete="off"
                requiredIndicator
              />
            </FormLayout.Group>
            <TextField label="Date" type="date" value={date} onChange={setDate} autoComplete="off" />
            <TextField
              label="Campaign (optional)"
              value={campaign}
              onChange={setCampaign}
              autoComplete="off"
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* CSV import */}
      <Modal
        open={importOpen}
        onClose={() => {
          setImportOpen(false);
          setCsvRows([]);
          setCsvError(null);
        }}
        title="Import ad spend from CSV"
        primaryAction={{
          content: csvRows.length ? `Import ${csvRows.length} rows` : 'Import',
          onAction: handleImportCsv,
          loading: saving,
          disabled: csvRows.length === 0,
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setImportOpen(false) }]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Banner tone="info">
              CSV columns (header optional): <b>date, platform, amount, campaign</b>. Date =
              YYYY-MM-DD; platform = facebook / google / tiktok / other.
            </Banner>
            <DropZone accept="text/csv,.csv" type="file" onDrop={handleDropCsv} allowMultiple={false}>
              <DropZone.FileUpload actionTitle="Choose CSV" actionHint="or drop a .csv file here" />
            </DropZone>
            {csvError && <Banner tone="warning">{csvError}</Banner>}
            {csvRows.length > 0 && (
              <BlockStack gap="100">
                <Text as="p" variant="bodySm" tone="subdued">
                  Preview ({csvRows.length} rows):
                </Text>
                <List type="bullet">
                  {csvRows.slice(0, 5).map((r, i) => (
                    <List.Item key={i}>
                      {r.date} · {r.platform} · {formatMoney(r.amount)}
                      {r.campaign_name ? ` · ${r.campaign_name}` : ''}
                    </List.Item>
                  ))}
                </List>
              </BlockStack>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>

      {toast && <Toast content={toast} onDismiss={() => setToast(null)} />}
    </Frame>
  );
}
